import { useTranslation } from 'react-i18next';
import { Body2, Normal2, Tiny1, Tiny2, Title4 } from 'components/Typography';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
import { ConnectButton } from 'components/ConnectButton';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { useConnection, useSwitchChain, useWalletClient } from 'wagmi';
import { Button } from 'baseui/button';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../../../migration/components/MigrationPanel/migrationpanel.module.css';
import { useSDAOLaunchpadClaim } from 'structure/claim/hooks/useSDAOLaunchpadClaim';
import { userClaimableList } from 'utils/constants/claim.const';
import { displayBalance } from 'utils/balance';
import { useUser } from 'structure/user/UserContext';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { NetworkChainIds } from '@dapp/multi-lib/network';

const InfoRow = ({
  label,
  value,
  highlight = false,
  symbol = '',
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
  symbol?: string;
}) => (
  <div className='mb-4 flex justify-between gap-5'>
    <Title4 className='font-normal text-white-500 lg:text-[0.875rem]'>
      {label}
    </Title4>
    <Title4
      className={`font-normal lg:text-[0.875rem] ${highlight ? 'text-lightYellow' : 'text-white-200'}`}
    >
      {value} {symbol && <Tiny2 className='text-white-500'>{symbol}</Tiny2>}
    </Title4>
  </div>
);

export const ClaimPagePanelItem = ({
  item,
}: {
  item: (typeof userClaimableList)[0];
}) => {
  const { t } = useTranslation('claim');
  const { switchChain } = useSwitchChain();
  const toastV2 = useToast();
  const csr = useClientRenderDelay();
  const { isConnected, chainId } = useConnection();
  const { account } = useUser();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: walletClient } = useWalletClient();
  const { data: claimData, refetch } = useSDAOLaunchpadClaim(
    item?.projectClaimContractAddress,
    item.poolId,
    item.poolChainId as NetworkChainIds
  );
  const checkSelectedPoolNetwork = item?.poolChainId !== chainId;
  const isClaimableAmountZero = Number(claimData?.claimableAmount) === 0;
  const isClaimAvailable = Boolean(claimData?.userInfo?.claimed);
  const buttonDisabled =
    !checkSelectedPoolNetwork &&
    (isClaimableAmountZero || isDisableButton || !claimData);
  const handleNetworkChange = () => {
    return switchChain?.({ chainId: item?.poolChainId });
  };
  const addTokenToMetamask = async () => {
    try {
      await walletClient?.watchAsset({
        type: 'ERC20',
        options: {
          address: item?.sfiToken,
          symbol: item?.symbol,
          decimals: item?.decimals,
        },
      });
    } catch (error) {
      console.info(error);
    }
  };

  const handleClaim = async () => {
    setIsLoading(true);
    try {
      if (!claimData?.launchPadClaimcontract || !account) {
        setIsLoading(false);
        return null;
      }
      if (isClaimableAmountZero) {
        setIsLoading(false);
        toastV2({
          title: t('common:ERROR_MESSAGES.ERROR'),
          description: t(
            'common:ERROR_MESSAGES.FAILED_DUE_TO_INSUFFICIENT_TOKEN'
          ),
          type: 'error',
        });
        return;
      }
      setIsDisableButton(true);
      const txn = await claimData!.claim();
      await txn.wait();
      await refetch();
      setIsLoading(false);
      setIsDisableButton(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.SUCCESS'),
        description: t('common:ERROR_MESSAGES.CLAIM_SUCCESSFUL'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error claiming tokens', error);
      setIsDisableButton(false);
      setIsLoading(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:ERROR_MESSAGES.CLAIM_ERROR'),
        type: 'error',
      });
    }
  };

  return (
    <div className='flex flex-col items-start gap-5 lg:flex-row lg:gap-24'>
      <div className='flex h-full w-full flex-col gap-5 border border-black-300 bg-black p-4 lg:p-8'>
        <Normal2 className='font-bold text-white-400'>
          {t('CLAIM_YOUR_TOKENS')}
        </Normal2>
        <div>
          <InfoRow
            label={t('ALLOCATION')}
            value={
              displayBalance(
                claimData?.userInfo?.allocated || '0',
                item?.decimals,
                true
              ) ?? 'N/A'
            }
            symbol={item?.symbol}
          />
          <InfoRow
            label={t('START_DATE')}
            value={claimData?.poolInfo?.emissionPeriod?.start ?? '---'}
          />
          <InfoRow
            label={t('END_DATE')}
            value={claimData?.poolInfo?.emissionPeriod?.end ?? '---'}
          />
          <InfoRow
            label={t('CLAIMED')}
            value={
              displayBalance(
                claimData?.userInfo?.claimed || '0',
                item?.decimals,
                true
              ) ?? 'N/A'
            }
            symbol={item?.symbol}
          />
          <InfoRow
            label={t('CLAIMABLE')}
            value={
              displayBalance(
                claimData?.claimableAmount || '0',
                item?.decimals,
                true
              ) ?? 'N/A'
            }
            symbol={item?.symbol}
            highlight
          />
          <div className='flex justify-end'>
            <button
              className='flex cursor-pointer items-center space-x-2 rounded-full text-white'
              onClick={addTokenToMetamask}
            >
              <Body2>{t('ADD_TO_WALLET')}</Body2>
              <Image
                alt='meta-mast'
                width={24}
                height={24}
                src={getStaticAssetUrl(
                  '/sfi/static/image/wallets/metamask.svg'
                )}
              />
            </button>
          </div>
        </div>
        {!isConnected && csr && (
          <Tiny1 className='text-negative-800'>
            {t('CONNECT_YOUR_WALLET_TO_SEE')}
          </Tiny1>
        )}
        {isConnected && csr && isClaimableAmountZero && !isClaimAvailable && (
          <Tiny1 className='text-negative-800'>
            {t('THE_WALLET_HAS_REWARDS_ALLOCATED')}{' '}
            <Tiny1 className='text-lightYellow'>
              {t('PLEASE_CONNECT_WALLET_USED_PARTICIPATE_SFI_TESTNET_CAMPAIGN')}
            </Tiny1>
          </Tiny1>
        )}
        {!isConnected && csr && (
          <div className='flex w-full flex-row justify-center'>
            <ConnectButton className='w-full px-10 py-1 text-sm hover:bg-white hover:text-black-1000' />
          </div>
        )}
        {isConnected && csr && (
          <Button
            disabled={buttonDisabled}
            isLoading={isLoading}
            className={cx(
              twMerge(
                styles.button,
                'w-full !p-2 !font-normal',
                buttonDisabled && '!bg-box-borderLight !text-mono-100'
              )
            )}
            onClick={
              checkSelectedPoolNetwork ? handleNetworkChange : handleClaim
            }
          >
            {checkSelectedPoolNetwork
              ? t('migrate:SWITCH_NETWORK')
              : t('CLAIM')}
          </Button>
        )}
      </div>
    </div>
  );
};

// Wrapper component that only renders if there's something to claim
const ClaimableItemWrapper = ({
  item,
  index,
  onVisibilityChange,
  onLoadingChange,
}: {
  item: (typeof userClaimableList)[0];
  index: number;
  onVisibilityChange: (index: number, visible: boolean) => void;
  onLoadingChange: (index: number, loading: boolean) => void;
}) => {
  const { chainId } = useUser();
  const { data: claimData, loading } = useSDAOLaunchpadClaim(
    item?.projectClaimContractAddress,
    item.poolId,
    item.poolChainId as NetworkChainIds
  );
  const checkSelectedPoolNetwork = item?.poolChainId !== chainId;
  const isClaimableAmountZero = Number(claimData?.claimableAmount) === 0;

  const hasClaimableOnTargetNetwork = useMemo(() => {
    if (!claimData) return false; // Still loading or no data
    return !isClaimableAmountZero;
  }, [claimData, isClaimableAmountZero]);

  const shouldShow = useMemo(() => {
    if (loading || !claimData) {
      return true; // Still loading, show it
    }
    if (checkSelectedPoolNetwork) {
      return hasClaimableOnTargetNetwork;
    }
    return !isClaimableAmountZero;
  }, [
    loading,
    claimData,
    checkSelectedPoolNetwork,
    isClaimableAmountZero,
    hasClaimableOnTargetNetwork,
  ]);

  useEffect(() => {
    onLoadingChange(index, loading);
  }, [index, loading, onLoadingChange]);

  useEffect(() => {
    if (!loading && claimData) {
      onVisibilityChange(index, shouldShow);
    }
  }, [index, shouldShow, loading, claimData, onVisibilityChange]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className='flex w-full flex-col gap-5'>
      <ClaimPagePanelItem item={item} />
    </div>
  );
};

export const ClaimPagePanel = () => {
  const { t } = useTranslation('claim');
  const { isConnected } = useConnection();
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const [loadingItems, setLoadingItems] = useState<Set<number>>(
    () => new Set(userClaimableList?.map((_, index) => index) || [])
  );

  const handleVisibilityChange = useCallback(
    (index: number, visible: boolean) => {
      setVisibleItems((prev) => {
        const next = new Set(prev);
        if (visible) {
          next.add(index);
        } else {
          next.delete(index);
        }
        return next;
      });
    },
    []
  );

  const handleLoadingChange = useCallback((index: number, loading: boolean) => {
    setLoadingItems((prev) => {
      const next = new Set(prev);
      if (loading) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  }, []);

  if (!userClaimableList || userClaimableList.length === 0) {
    return (
      <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/50 p-16'>
        <div className='mb-6 flex items-center justify-center'>
          <div className='rounded-full bg-white/5 p-6'>
            <Icon
              icon='claim'
              width={64}
              height={64}
              className='text-white-400'
            />
          </div>
        </div>
        <Normal2 className='mb-2 text-center font-semibold text-white'>
          {t('NO_CLAIMABLE_TOKENS') || 'No claimable tokens available'}
        </Normal2>
        <Body2 className='max-w-md text-center text-white-500'>
          {t('NO_CLAIMABLE_TOKENS_DESCRIPTION') ||
            'There are currently no tokens available to claim. Check back later or connect a different wallet.'}
        </Body2>
      </div>
    );
  }

  // Show empty state if wallet is not connected
  if (!isConnected) {
    return (
      <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/50 p-16'>
        <div className='mb-6 flex items-center justify-center'>
          <div className='rounded-full bg-white/5 p-6'>
            <Icon
              icon='claim'
              width={64}
              height={64}
              className='text-white-400'
            />
          </div>
        </div>
        <Normal2 className='mb-2 text-center font-semibold text-white'>
          {t('NO_CLAIMABLE_TOKENS') || 'No claimable tokens available'}
        </Normal2>
        <Body2 className='max-w-md text-center text-white-500'>
          {t('NO_CLAIMABLE_TOKENS_DESCRIPTION') ||
            'There are currently no tokens available to claim. Check back later or connect a different wallet.'}
        </Body2>
      </div>
    );
  }

  const renderedItems = userClaimableList.map((item, index) => (
    <ClaimableItemWrapper
      key={item.projectClaimContractAddress + index}
      item={item}
      index={index}
      onVisibilityChange={handleVisibilityChange}
      onLoadingChange={handleLoadingChange}
    />
  ));

  // Check if all items have finished loading
  const allItemsLoaded = loadingItems.size === 0;
  // Check if any items are visible
  const hasVisibleItems = visibleItems.size > 0;
  console.log('allItemsLoaded', allItemsLoaded);
  // Show empty state only if all items have loaded and none are visible
  if (allItemsLoaded && !hasVisibleItems && userClaimableList.length > 0) {
    return (
      <div className='flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-black/50 p-16'>
        <div className='mb-6 flex items-center justify-center'>
          <div className='rounded-full bg-white/5 p-6'>
            <Icon
              icon='claim'
              width={64}
              height={64}
              className='text-white-400'
            />
          </div>
        </div>
        <Normal2 className='mb-2 text-center font-semibold text-white'>
          {t('NO_CLAIMABLE_TOKENS') || 'No claimable tokens available'}
        </Normal2>
        <Body2 className='max-w-md text-center text-white-500'>
          {t('NO_CLAIMABLE_TOKENS_DESCRIPTION') ||
            'There are currently no tokens available to claim. Check back later or connect a different wallet.'}
        </Body2>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 lg:gap-5'>
      {renderedItems}
    </div>
  );
};
